// src/components/LiveListingsPage.js product and clone listings
import React, { useState, useEffect, useMemo } from 'react';

// Joy UI Components
import Box from '@mui/joy/Box';
import Grid from '@mui/joy/Grid';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Typography from '@mui/joy/Typography';
import Switch from '@mui/joy/Switch';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Checkbox from '@mui/joy/Checkbox';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import Divider from '@mui/joy/Divider';
import Avatar from '@mui/joy/Avatar';
import Tooltip from '@mui/joy/Tooltip';
import CircularProgress from '@mui/joy/CircularProgress';
import Stack from '@mui/joy/Stack';
import Snackbar from '@mui/joy/Snackbar';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';
import { useColorScheme } from '@mui/joy/styles';
import { motion, AnimatePresence } from 'framer-motion';

import MDEditor from '@uiw/react-md-editor';
import { listSeeds, updateSeed } from '../api/allSeeds';

// Icons
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import OpacityRoundedIcon from '@mui/icons-material/OpacityRounded';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

export default function LiveListingsPage() {
  const { mode: colorMode } = useColorScheme();
  
  const [seeds, setSeeds]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState(null);
  const [mode, setMode]               = useState(0); // 0 for seeds, 1 for clones (using tab index)
  const [collectionFilter, setCollectionFilter] = useState([]);
  const [stockFilter, setStockFilter]           = useState([]);
  const [activeFilter, setActiveFilter]         = useState([]);
  const [saving, setSaving]                     = useState(false);
  const [snackbar, setSnackbar]                 = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCollections, setExpandedCollections] = useState({});

  // Get type-specific color - using listings color from navbar
  const getTypeColor = () => {
    return '#ff9800'; // Orange color to match theme
  };
  
  const typeColor = getTypeColor();

  useEffect(() => {
    (async () => {
      try {
      const all = await listSeeds();
        setSeeds(all || []);
      } catch (error) {
        console.error("Error fetching seeds:", error);
        setSnackbarMessage(`Failed to load product listings: ${error.message}`);
        setSnackbarOpen(true);
        setSeeds([]);
      } finally {
      setLoading(false);
      }
    })();
  }, []);

  const collections = useMemo(() => {
    const cols = Array.from(new Set(seeds.map(s => s.collection)));
    return mode === 0 ? cols.filter(c => c !== 'clones') : [];
  }, [seeds, mode]);

  const baseList = useMemo(
    () =>
      seeds.filter(s =>
        mode === 1
          ? s.collection === 'clones'
          : s.collection !== 'clones'
      ),
    [seeds, mode]
  );

  const filtered = useMemo(() => {
    return baseList.filter(s => {
      const instock = s.inventory > 0 && s.isActive;
      const lowercaseTerm = searchTerm.toLowerCase().trim();

      // Comprehensive search
      const matchesSearch = !lowercaseTerm || (
        s.name.toLowerCase().includes(lowercaseTerm) ||
        s.description.toLowerCase().includes(lowercaseTerm) ||
        s.collection.toLowerCase().includes(lowercaseTerm) ||
        s.fullDescription?.toLowerCase().includes(lowercaseTerm)
      );

      if (mode === 0 && collectionFilter.length) {
        if (!collectionFilter.includes(s.collection)) return false;
      }
      if (stockFilter.length) {
        if (
          !(
            (stockFilter.includes('InStock') && instock) ||
            (stockFilter.includes('OutStock') && !instock)
          )
        )
          return false;
      }
      if (activeFilter.length) {
        if (
          !(
            (activeFilter.includes('Active') && s.isActive) ||
            (activeFilter.includes('Inactive') && !s.isActive)
          )
        )
          return false;
      }
      return matchesSearch;
    });
  }, [baseList, collectionFilter, stockFilter, activeFilter, mode, searchTerm]);

  const displayed = useMemo(() => {
    const arr = [...filtered];
    if (mode === 0) {
      arr.sort((a, b) => {
        if (a.collection < b.collection) return -1;
        if (a.collection > b.collection) return 1;
        return a.name.localeCompare(b.name);
      });
    } else {
      arr.sort((a, b) => a.name.localeCompare(b.name));
    }
    return arr;
  }, [filtered, mode]);

  const resetFilters = () => {
    setCollectionFilter([]);
    setStockFilter([]);
    setActiveFilter([]);
    setSearchTerm('');
  };

  const handleSave = async updates => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateSeed(selected.id, selected.createdDate, updates);
      const all = await listSeeds();
      setSeeds(all);
      setSelected(null);
      
      // Show success message
      setSnackbar({
        open: true,
        message: `${selected.name} updated successfully. Inventory changes will be reflected in the inventory tables.`,
        severity: 'success'
      });
    } catch (error) {
      console.error("Error updating seed:", error);
      // Show error message
      setSnackbar({
        open: true,
        message: `Failed to update ${selected.name}. Please try again.`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Group items by collection for display
  const groupedItems = useMemo(() => {
    if (mode === 1) return { Clones: displayed };
    
    return displayed.reduce((acc, item) => {
      const collection = item.collection || 'Uncategorized';
      if (!acc[collection]) {
        acc[collection] = [];
      }
      acc[collection].push(item);
      return acc;
    }, {});
  }, [displayed, mode]);

  // Sort collections alphabetically
  const sortedCollections = Object.keys(groupedItems).sort();

  // Initialize expanded state for all collections
  useEffect(() => {
    const initialExpanded = {};
    sortedCollections.forEach(collection => {
      initialExpanded[collection] = true; // Start with all expanded
    });
    setExpandedCollections(initialExpanded);
  }, [sortedCollections.join(',')]);

  const toggleCollection = (collection) => {
    setExpandedCollections(prev => ({
      ...prev,
      [collection]: !prev[collection]
    }));
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      pt: 10,
      pb: 4,
      px: { xs: 2, sm: 3, md: 4 },
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
    }}>
      {/* Header */}
      <Sheet
        variant="soft"
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          borderRadius: 'xl',
          background: 'linear-gradient(135deg, #C0C0C0 0%, #E5E5E5 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center',
          mb: 4
        }}
      >
            <Typography 
          level="h1" 
              sx={{ 
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 'bold',
            color: '#333333',
            mb: { xs: 1, sm: 1.5 },
            textAlign: 'center',
            lineHeight: { xs: 1.2, sm: 1.1 }
          }}
        >
          Live Product Listings
            </Typography>
        <Typography 
          level="body-lg" 
              sx={{
            color: '#666666',
            textAlign: 'center',
            maxWidth: 600,
            mx: 'auto',
            fontSize: { xs: '0.95rem', sm: '1.125rem' },
            lineHeight: { xs: 1.4, sm: 1.5 },
            px: { xs: 1, sm: 0 }
          }}
        >
          Manage your live product listings and inventory
        </Typography>
      </Sheet>

      {/* Unified Control Bar - Everything in One Place */}
      <Sheet 
        variant="soft"
        sx={{
          p: { xs: 2, sm: 3 }, 
          mb: 3, 
          borderRadius: 'xl',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        {/* Top Row: Tabs + Search - Centered Layout */}
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={{ xs: 2, md: 4 }}
          alignItems="center"
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          {/* Tabs - Centered */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            width: { xs: '100%', md: 'auto' }
          }}>
            <Sheet
              variant="soft"
                  sx={{
                borderRadius: 'lg',
                background: 'rgba(255, 255, 255, 0.08)',
                p: 0.5,
                display: 'flex',
                gap: 0.5
              }}
            >
              <Button
                variant={mode === 0 ? 'solid' : 'soft'}
                onClick={() => setMode(0)}
                startDecorator={<SpaRoundedIcon />}
                sx={{
                  borderRadius: 'md',
                  px: { xs: 1.5, sm: 2 },
                  py: 1,
                  fontWeight: 'bold',
                  minWidth: { xs: 100, sm: 120 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  ...(mode === 0 ? {
                    background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
                  } : {
                    color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    }
                  })
                }}
              >
                Seeds
              </Button>
              <Button
                variant={mode === 1 ? 'solid' : 'soft'}
                onClick={() => setMode(1)}
                startDecorator={<OpacityRoundedIcon />}
                sx={{
                  borderRadius: 'md',
                  px: { xs: 1.5, sm: 2 },
                  py: 1,
                  fontWeight: 'bold',
                  minWidth: { xs: 100, sm: 120 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  ...(mode === 1 ? {
                    background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
                  } : {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    }
                  })
                }}
              >
                Clones
              </Button>
            </Sheet>
        </Box>
        
          {/* Search Bar - Centered */}
          <Box sx={{ 
            width: { xs: '100%', md: 'auto' },
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Input
              placeholder="Search by name, description, or collection..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startDecorator={<SearchIcon />}
                  sx={{
                width: { xs: '100%', md: 350 },
                borderRadius: 'lg',
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                  borderColor: 'rgba(255, 152, 0, 0.5)'
                },
                '&:focus-within': {
                  borderColor: '#ff9800'
                }
              }}
            />
          </Box>
        </Stack>
        
        {/* Bottom Row: Filters */}
        <Stack 
          direction="row"
          spacing={1}
          alignItems="flex-end"
          justifyContent="center"
                sx={{
            flexWrap: 'wrap',
            gap: 1,
            '@media (max-width: 600px)': {
              justifyContent: 'center'
            }
          }}
        >
          {mode === 0 && (
            <FormControl sx={{ minWidth: { xs: 110, sm: 140 } }}>
              <FormLabel sx={{ 
                color: 'white', 
                fontSize: '0.75rem', 
                textAlign: 'center', 
                mb: 0.5,
                display: 'block',
                width: '100%'
              }}>
                Collection
              </FormLabel>
            <Select
                multiple
              value={collectionFilter}
                onChange={(e, newValue) => setCollectionFilter(newValue || [])}
                placeholder="All"
                size="sm"
                        sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 'md',
                  color: 'white',
                  minHeight: 32,
                  '&:hover': {
                    borderColor: 'rgba(255, 152, 0, 0.5)'
                  }
                }}
              >
                {collections.map(col => (
                  <Option key={col} value={col}>
                    {col}
                  </Option>
              ))}
            </Select>
          </FormControl>
          )}

          <FormControl sx={{ minWidth: { xs: 110, sm: 140 } }}>
            <FormLabel sx={{ 
              color: 'white', 
              fontSize: '0.75rem', 
              textAlign: 'center', 
              mb: 0.5,
              display: 'block',
              width: '100%'
            }}>
              Stock
            </FormLabel>
          <Select
              multiple
            value={stockFilter}
              onChange={(e, newValue) => setStockFilter(newValue || [])}
              placeholder="All"
              size="sm"
                      sx={{
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 'md',
                color: 'white',
                minHeight: 32,
                '&:hover': {
                  borderColor: 'rgba(255, 152, 0, 0.5)'
                }
              }}
            >
              <Option value="InStock">In Stock</Option>
              <Option value="OutStock">Out of Stock</Option>
          </Select>
        </FormControl>

          <FormControl sx={{ minWidth: { xs: 110, sm: 140 } }}>
            <FormLabel sx={{ 
              color: 'white', 
              fontSize: '0.75rem', 
              textAlign: 'center', 
              mb: 0.5,
              display: 'block',
              width: '100%'
            }}>
              Status
            </FormLabel>
          <Select
              multiple
            value={activeFilter}
              onChange={(e, newValue) => setActiveFilter(newValue || [])}
              placeholder="All"
              size="sm"
                      sx={{
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 'md',
                color: 'white',
                minHeight: 32,
                '&:hover': {
                  borderColor: 'rgba(255, 152, 0, 0.5)'
                }
              }}
            >
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
          </Select>
        </FormControl>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: { xs: 80, sm: 100 }
          }}>
            <Box sx={{ height: '20px', mb: 0.5 }} /> {/* Spacer to align with other controls */}
            <Button
              variant="outlined"
              startDecorator={<RestartAltIcon />}
              onClick={resetFilters}
              size="sm"
              sx={{
                borderColor: 'rgba(255, 152, 0, 0.5)',
                color: '#ff9800',
                borderRadius: 'md',
                minHeight: 32,
                px: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                '&:hover': {
                  borderColor: '#ff9800',
                  bgcolor: 'rgba(255, 152, 0, 0.1)'
                }
              }}
            >
              Reset
            </Button>
          </Box>
        </Stack>
      </Sheet>
      
      {/* Loading indicator */}
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 400 
        }}>
          <CircularProgress size="lg" sx={{ color: typeColor }} />
        </Box>
      ) : displayed.length === 0 ? (
        <Box sx={{ 
          textAlign: 'center', 
          p: 5, 
          bgcolor: 'rgba(255, 255, 255, 0.05)', 
          borderRadius: 'xl',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Typography level="h3" sx={{ mb: 1, color: 'white' }}>
            No listings match your filter criteria
          </Typography>
        </Box>
      ) : (
        <>
          {/* Collapsible Collections */}
          {sortedCollections.map(collection => (
            <Box key={collection} sx={{ mb: 4 }}>
              <Accordion
                expanded={expandedCollections[collection] || false}
                onChange={() => toggleCollection(collection)}
                sx={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 'xl',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  sx={{
                    minHeight: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& .MuiAccordionSummary-content': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      width: '100%',
                      margin: 0
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      position: 'absolute',
                      right: 16
                    }
                  }}
                >
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%'
              }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <LocalOfferIcon sx={{ color: typeColor }} />
                      <Typography level="h3" fontWeight="bold" sx={{ color: typeColor }}>
                  {collection}
                </Typography>
                <Chip 
                        size="sm" 
                  sx={{ 
                          bgcolor: 'rgba(255, 152, 0, 0.2)',
                    color: 'white',
                          fontWeight: 'bold',
                          border: '1px solid rgba(255, 152, 0, 0.3)'
                  }} 
                      >
                        {groupedItems[collection].length}
                      </Chip>
                    </Stack>
      </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={3}>
                    <AnimatePresence>
                {groupedItems[collection].map(s => (
                        <Grid key={s.id + s.createdDate} xs={12} sm={6} md={4} lg={3}>
                          <MotionCard
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            whileHover={{ 
                              scale: 1.02,
                              transition: { type: 'spring', stiffness: 300, damping: 20 }
                            }}
                            variant="soft"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                              borderRadius: 'xl',
                        overflow: 'hidden',
                              background: 'rgba(255, 255, 255, 0.05)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              cursor: 'pointer',
                        '&:hover': { 
                          transform: 'translateY(-8px)',
                                boxShadow: '0 12px 24px rgba(255, 152, 0, 0.2)',
                                border: '1px solid rgba(255, 152, 0, 0.3)'
                        }
              }}
                onClick={() => setSelected(s)}
              >
                            {/* Image */}
                          {s.imageUrl ? (
                  <Box
                    component="img"
                    src={s.imageUrl}
                    alt={s.name}
                    sx={{
                      width: '100%',
                                  height: 200,
                                objectFit: 'cover',
                                transition: 'transform 0.5s ease',
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: '100%',
                                  height: 200,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                  bgcolor: 'rgba(255, 152, 0, 0.1)'
                              }}
                            >
                                <Typography level="body-sm" color="text.secondary">
                                No Image
                              </Typography>
                            </Box>
                          )}
                          
                            <CardContent sx={{ p: 2, flexGrow: 1 }}>
                              {/* Header */}
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography level="title-md" sx={{ fontWeight: 'bold', mb: 0.5, color: 'white', textAlign: 'center' }}>
                    {s.name}
                  </Typography>
                                  {s.description && (
                  <Typography
                                      level="body-sm" 
                              sx={{
                                        color: 'text.secondary',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textAlign: 'center'
                              }}
                  >
                    {s.description}
                  </Typography>
                                  )}
                          </Box>
                              </Stack>

                              {/* Status chips - Centered */}
                              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <Stack direction="column" spacing={0.5} alignItems="center">
                                  <Chip
                                    size="sm"
                                    color={s.inventory > 0 && s.isActive ? 'success' : 'danger'}
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    {s.inventory > 0 && s.isActive ? 'In Stock' : 'Out of Stock'}
                                  </Chip>
                                  
                                  {!s.isActive && (
                                    <Chip
                                      startDecorator={<VisibilityOffIcon />}
                                      size="sm"
                                      sx={{
                                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      Inactive
                                    </Chip>
                                  )}
                                </Stack>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              {/* Price and Edit Button - Centered */}
                          <Box sx={{ 
                            display: 'flex', 
                                justifyContent: 'center',
                            alignItems: 'center',
                                mt: 'auto',
                                flexDirection: 'column',
                                gap: 2
                          }}>
                            {s.price ? (
                                  <Typography level="title-lg" fontWeight="bold" sx={{ color: typeColor }}>
                                ${s.price.toFixed(2)}
                              </Typography>
                            ) : (
                                  <Typography level="body-sm" color="text.secondary">
                                No price
                              </Typography>
                            )}
                            
                            <Tooltip title="Edit Details">
                              <IconButton 
                                    size="sm"
                                    variant="soft"
                                sx={{ 
                                  color: typeColor,
                                      bgcolor: 'rgba(255, 152, 0, 0.1)',
                                  '&:hover': {
                                        bgcolor: 'rgba(255, 152, 0, 0.2)'
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelected(s);
                                }}
                              >
                                    <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                </CardContent>
                          </MotionCard>
          </Grid>
        ))}
                    </AnimatePresence>
      </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>
          ))}
        </>
      )}

      {/* Edit Modal */}
      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <ModalDialog
          variant="soft"
              sx={{ 
            maxWidth: 'md',
            width: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            background: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
          <ModalClose sx={{ color: 'white' }} />
          
          {selected && (
            <EditSeedForm 
              seed={selected} 
              onSave={handleSave} 
              saving={saving}
              onClose={() => setSelected(null)}
            />
          )}
        </ModalDialog>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        color={snackbar.severity}
      >
        {snackbar.message}
      </Snackbar>
                              </Box>
  );
}

// Edit Seed Form Component
function EditSeedForm({ seed, onSave, saving, onClose }) {
  const [formData, setFormData] = useState({
    name: seed.name || '',
    description: seed.description || '',
    fullDescription: seed.fullDescription || '',
    price: seed.price || 0,
    inventory: seed.inventory || 0,
    isActive: seed.isActive || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography level="h4" sx={{ mb: 3, color: 'white', textAlign: 'center' }}>
        Edit: {seed.name}
                      </Typography>
      
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <FormControl>
            <FormLabel sx={{ color: 'white' }}>Name</FormLabel>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </FormControl>
          
          <FormControl>
            <FormLabel sx={{ color: 'white' }}>Description</FormLabel>
            <Input
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          />
          </FormControl>
          
          <FormControl>
            <FormLabel sx={{ color: 'white' }}>Price ($)</FormLabel>
            <Input
                              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
            />
          </FormControl>
          
          <FormControl>
            <FormLabel sx={{ color: 'white' }}>Inventory</FormLabel>
            <Input
              type="number"
              value={formData.inventory}
              onChange={(e) => setFormData(prev => ({ ...prev, inventory: parseInt(e.target.value) || 0 }))}
                            />
          </FormControl>
          
          <FormControl>
            <Checkbox
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              label="Active"
              sx={{ color: 'white' }}
            />
          </FormControl>
                      
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button
              type="submit" 
              variant="solid" 
              loading={saving}
              startDecorator={<SaveIcon />}
              >
              Save Changes
              </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}
